
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import dayjs, { Dayjs } from "dayjs";

// mui components
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import FormLabel from "@mui/material/FormLabel";
import FormControl from "@mui/material/FormControl";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import Button from "@mui/material/Button";
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar';
import { PickerValue } from "@mui/x-date-pickers/internals/models";

// Context
import { useCoursesNewContext } from "../../../context";

// Styles
import styles from "./style.module.scss";

// Utils
import * as urls from "../../../../../../utils/urls";

// custom hooks
import useCSRFToken from "../../../../../../utils/hooks/csfrToken";

// Types
import { Unit, KeyDate } from "../../../../../../types";
import Typography from "@mui/material/Typography";

interface AddToStructureProps {
  open: boolean;
  date_type?: 'unit' | 'key_date';
  data?: Unit | KeyDate;
  onClose: () => void;
  onSave?: (success: boolean) => void;
}

const AddToStructure: React.FC<AddToStructureProps> = ({
  open,
  date_type,
  data,
  onClose,
  onSave
}) => {
  // Hooks
  const { t } = useTranslation();
  const csrfToken = useCSRFToken();

  const { course } = useCoursesNewContext();

  // state
  const [type, setType] = useState<'unit' | 'key_date'>('unit');
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [errors, setErrors] = useState<{ title?: boolean, date?: boolean }>({});

  // form
  const [id, setId] = useState<string | number | null>(null);
  const [title, setTitle] = useState<string>('');
  const [startDate, setStartDate] = useState<Dayjs>(dayjs());

  useEffect(() => {
    if (data && date_type) {
      setIsEdit(true);
      if (date_type === 'unit') {
        const unit = data as Unit;
        setId(unit.id);
        setTitle(unit.title);
        setStartDate(dayjs(unit.start_date));
      } else {
        const keyDate = data as KeyDate;
        setId(keyDate.id);
        setTitle(keyDate.name);
        setStartDate(dayjs(keyDate.date));
      }
    }
  }, [data, date_type]);

  // Memoized values
  const maxSelectableDate = useMemo(() => {
    if (course?.end_date) return dayjs(course.end_date);
    else return undefined;
  }, [course]);

  const minSelectableDate = useMemo(() => {
    if (course?.start_date) return dayjs(course.start_date);
    else return undefined;
  }, [course]);

  const courseId = useMemo(() => {
    if (course?.id) return course.id;
    else return 'INVALID_COURSE_ID';
  }, [course]);

  // Methods
  const resetForm = useCallback(() => {
    setIsEdit(false);
    setId(null);
    setTitle('');
    setStartDate(dayjs());
  }, []);

  const saveUnit = useCallback(async () => {
    setLoading(true);
    try {
      const body = new FormData();
      const url = isEdit ? urls.unitUrl(courseId, id || '') : urls.unitsUrl(courseId);

      body.append('unit[title]', title);
      body.append('unit[start_date]', startDate?.format('YYYY-MM-DD') ?? '');
      body.append('unit[description]', '');

      const options = {
        method: isEdit ? 'PATCH' : 'POST',
        headers: { 'X-CSRF-TOKEN': csrfToken },
        body
      }

      const response = await fetch(url, options);
      if (response.ok && onSave) {
        onSave(response.ok);
        resetForm();
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [csrfToken, data, isEdit, courseId, title, startDate, id, onSave, resetForm]);

  const saveKeyDate = useCallback(async () => {
    setLoading(true);
    try {
      const body = new FormData();
      const url = isEdit ? urls.courseKeyDateUrl(courseId, id || '') : urls.courseKeyDatesUrl(courseId);

      body.append('key_date[name]', title);
      body.append('key_date[date]', startDate?.format('YYYY-MM-DD') ?? '');
      body.append('key_date[description]', '');

      const options = {
        method: isEdit ? 'PATCH' : 'POST',
        headers: { 'X-CSRF-TOKEN': csrfToken },
        body
      }

      const response = await fetch(url, options);
      if (response.ok && onSave) {
        onSave(response.ok);
        resetForm();
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [csrfToken, data, isEdit, courseId, title, startDate, id, onSave, resetForm]);

  // handlers
  const handleDateChange = useCallback((value: PickerValue) => {
    if (value) {
      if (startDate.isSame(value)) setStartDate(dayjs());
      else setStartDate(value as Dayjs);
    }
  }, [startDate]);
  const handleTitleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setTitle(value);
    setErrors({ title: value.length < 3 });
  }, []);

  const handleSave = useCallback(async () => {
    const errors = {
      title: !title,
      date: !startDate
    }
    setErrors(errors);

    if (errors.title || errors.date) return;

    if (type === 'unit') {
      await saveUnit();
    } else {
      await saveKeyDate();
    }
  }, [title, startDate, type]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='sm'
      fullWidth
    >
      <DialogTitle className={styles.title_container}>
        {isEdit ? t('courses.new.structure.edit_date') : t('courses.new.structure.add_date')}

        <IconButton
          aria-label='close'
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent className={styles.content}>
        {!isEdit && (
          <FormControl>
            <FormLabel>
              {t('courses.new.structure.type_label')}
            </FormLabel>

            <Box className={styles.type_container}>
              <FormControlLabel
                control={<Checkbox checked={type === 'unit'} onChange={() => setType('unit')} />}
                label={t('courses.new.structure.type_unit')}
                className={styles.date_label}
              />

              <FormControlLabel
                control={<Checkbox checked={type === 'key_date'} onChange={() => setType('key_date')} />}
                label={t('courses.new.structure.type_test')}
                className={styles.date_label}
              />
            </Box>
          </FormControl>
        )}

        <FormControl>
          <FormLabel>
            {t('courses.new.structure.title_label')}
            <span className={styles.required}>*</span>
          </FormLabel>

          <TextField
            variant='outlined'
            placeholder={t('courses.new.structure.title_placeholder')}
            value={title}
            onChange={handleTitleChange}
            fullWidth
            error={errors.title}
            helperText={errors.title && t('courses.new.structure.title_error')}
          />
        </FormControl>

        <FormControl>
          <FormLabel>
            {t('courses.new.structure.date_label')}
          </FormLabel>

          <TextField
            variant='outlined'
            placeholder={t('courses.new.structure.date_placeholder')}
            value={startDate?.format('DD.MM.YYYY')}
            fullWidth
            error={errors.date}
            helperText={errors.date && t('courses.new.structure.date_error')}
            slotProps={{
              input: {
                readOnly: true,
              }
            }}
          />

          <DateCalendar
            value={startDate}
            onChange={handleDateChange}
            maxDate={maxSelectableDate}
            minDate={minSelectableDate}
          />
        </FormControl>
      </DialogContent>

      <DialogActions className={styles.actions}>
        <Button
          variant='contained'
          onClick={handleSave}
          disabled={loading}
          classes={{
            root: styles.save_button
          }}
        >
          {loading ? <CircularProgress size={20} /> : t('actions.save')}
        </Button>
      </DialogActions>
    </Dialog>
  )
};

export default AddToStructure;
